import React, { useState } from 'react';
import { track } from '../../tracking';
import { Checkbox, Col, DatePicker, Radio } from 'antd';
import moment, { Moment } from 'moment';
import { Tags, UIDocumentEntity } from '../../pages/MedicalRecordsRightPanel';
import { OPTIONS } from '../common/MedicalRecordFileTypeSelector';

const { RangePicker } = DatePicker;

export type DateRange = {
    startDate?: string;
    endDate?: string;
};

export function FilterDialogV2<FilterBasisType extends string | number>({
    activeTags,
    tags,
    onApplyFilter,
    toggleFilterDialog,
    recordUploadedDate,
    documntDate,
    tabs,
    selectedTabIndex,
    wrapTitle = false,
}: {
    activeTags?: Tags;
    tags?: Tags;
    toggleFilterDialog: (visible?: boolean | undefined) => void;
    recordUploadedDate?: DateRange;
    documntDate?: DateRange;
    onApplyFilter: (
        recordUploaded?: DateRange,
        documentDate?: DateRange,
        activeTags?: Tags,
        selectedTabIndex?: number,
    ) => void;
    tabs: {
        label: string;
        filter: (list: UIDocumentEntity[]) => UIDocumentEntity[];
    }[];
    selectedTabIndex: number;
    wrapTitle?: boolean;
}): JSX.Element {
    const serviceMap = tabs.map((t) => t.label);
    const [selectedService, setSelectedService] = useState(
        tabs?.[selectedTabIndex]?.label || tabs[0].label,
    );
    const [recordUploaded, setRecordUploaded] = useState<DateRange | undefined>(recordUploadedDate);

    const [documentDate, setDocumentDate] = useState<DateRange | undefined>(documntDate);

    const [activeTag, setActiveTag] = useState(activeTags);

    const setActiveTags = (name: string) => {
        setActiveTag((prev) => ({ ...prev, [name]: 1 }));
    };

    const removeActiveTags = (name: string) => {
        const tags = { ...activeTag };
        delete tags?.[name];

        setActiveTag(tags);
    };

    const disabledDate = (current: Moment | null): boolean => {
        const maxDate = moment();
        return current ? current > maxDate : false;
    };

    const onApply = () => {
        const index = serviceMap.findIndex((s) => s === selectedService);
        onApplyFilter(recordUploaded, documentDate, activeTag, index >= 0 ? index : undefined);
        toggleFilterDialog(false);
    };

    const onClearAllFilters = () => {
        setRecordUploaded(undefined);
        setDocumentDate(undefined);
        setActiveTag(undefined);
        setSelectedService(tabs[0].label);
    };

    const toggle = (s: string) => {
        setSelectedService(s);
    };

    return (
        <>
            <div
                className="modal-overlay"
                onClick={(e) => {
                    e.stopPropagation();
                    toggleFilterDialog(false);
                }}
            ></div>
            <div className="modal-content flex flex-col max-h-592 h-2/3 font-lato font-400 rounded-8 w-40p">
                <div className="flex items-center justify-between p-16 border-b bg-neutral-9 rounded-t-8 border-neutral-7">
                    <p className="font-700 text-16">Filter records</p>

                    <div className="flex items-center space-x-16">
                        <span
                            onClick={onClearAllFilters}
                            className="text-darwin-accent-bright-blue-800 font-700 cursor-pointer"
                        >
                            Clear all
                        </span>
                        <div
                            className="flex items-center justify-center w-16 h-16 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleFilterDialog(false);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                className="w-18 h-18 fill-neutral-5 hover:fill-neutral-black"
                            >
                                <path d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="overflow-scroll p-16 space-y-8">
                    <div className="flex items-center space-x-36">
                        <div>
                            <div className="text-13 font-700">Record uploaded</div>
                            <div className="flex items-center space-x-8 py-12">
                                <RangePicker
                                    value={
                                        recordUploaded
                                            ? [
                                                  moment(recordUploaded?.startDate),
                                                  moment(recordUploaded?.endDate),
                                              ]
                                            : undefined
                                    }
                                    onChange={(date) => {
                                        if (date) {
                                            setRecordUploaded({
                                                startDate: date[0]?.toISOString(),
                                                endDate: date[1]?.toISOString(),
                                            });
                                        } else {
                                            setRecordUploaded(undefined);
                                        }
                                    }}
                                    disabledDate={disabledDate}
                                />
                            </div>
                        </div>
                        <div>
                            <div className="text-13 font-700">Document date</div>
                            <div className="flex items-center space-x-8 py-12">
                                <RangePicker
                                    value={
                                        documentDate
                                            ? [
                                                  moment(documentDate?.startDate),
                                                  moment(documentDate?.endDate),
                                              ]
                                            : undefined
                                    }
                                    onChange={(date) => {
                                        if (date) {
                                            setDocumentDate({
                                                startDate: date[0]?.toISOString(),
                                                endDate: date[1]?.toISOString(),
                                            });
                                        } else {
                                            setDocumentDate(undefined);
                                        }
                                    }}
                                    disabledDate={disabledDate}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="w-full pb-12 bg-white space-y-8">
                        <div className="text-13 font-700">Files </div>
                        <Radio.Group
                            value={selectedService}
                            onChange={(e) => {
                                toggle(e.target.value);
                            }}
                        >
                            {[...serviceMap].map((s) => (
                                <Radio value={s}>{s}</Radio>
                            ))}
                        </Radio.Group>
                    </div>

                    {!!Object.keys(tags || {}).length && (
                        <div>
                            <div className="text-13 font-700">Tags added</div>
                            <div className="flex flex-wrap gap-8 pt-12">
                                {Object.keys(tags || {})?.map((key) => {
                                    const isSelected = activeTag?.[key];
                                    return (
                                        <div
                                            className={`${
                                                isSelected
                                                    ? 'font-700 bg-darwin-accent-bright-blue-100 text-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                                                    : 'bg-white text-darwin-neutral-1000 border-darwin-neutral_new-200'
                                            } flex items-center space-x-8 whitespace-nowrap text-13 px-12 py-8 rounded-8 border cursor-pointer`}
                                            onClick={() => setActiveTags(key)}
                                        >
                                            <span>
                                                {(
                                                    OPTIONS?.find((o) => o.value === key)?.label ||
                                                    key
                                                )?.replace(/^RT#/, '')}{' '}
                                                ({tags?.[key]})
                                            </span>
                                            {isSelected && (
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeActiveTags(key);
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="11"
                                                        height="12"
                                                        viewBox="0 0 11 12"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M10.6582 2.28125L6.9082 6.03125L10.627 9.75C10.9395 10.0312 10.9395 10.5 10.627 10.7812C10.3457 11.0938 9.87695 11.0938 9.5957 10.7812L5.8457 7.0625L2.12695 10.7812C1.8457 11.0938 1.37695 11.0938 1.0957 10.7812C0.783203 10.5 0.783203 10.0312 1.0957 9.71875L4.81445 6L1.0957 2.28125C0.783203 2 0.783203 1.53125 1.0957 1.21875C1.37695 0.9375 1.8457 0.9375 2.1582 1.21875L5.87695 4.96875L9.5957 1.25C9.87695 0.9375 10.3457 0.9375 10.6582 1.25C10.9395 1.53125 10.9395 2 10.6582 2.28125Z"
                                                            fill="#1A1A1A"
                                                        />
                                                    </svg>
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex bg-white mt-auto w-full items-center justify-between p-16 border-t border-neutral-7">
                    <button
                        className="px-16 py-8 btn--small secondary-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleFilterDialog(false);
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-16 py-8 btn--small primary-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onApply();
                        }}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </>
    );
}
